import { ThunkDispatch as Dispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { IErrors, IHistogramData, IItemsOptions } from '../../../core/interfaces';
import { GraphAndStateDataService } from '../../../core/services';
import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import store from '../../../core/store';
import { selectScreenWidth } from '../../../core/selectors/dashboard/dashboardSelector';
import { selectGraphDataAll, selectGraphDataRangeAll } from '../../../core/selectors/graphData/graphDataSelector';
import { poolingGaps } from '../../../helpers';
import { graphDataMapperConstants } from '../../../core/constants/graphDataMapperConstants';
import { getAllSensor } from '../../../core/selectors/monitoringTree/minimapGetActiveSensorInTreeSelector';
import chunk from 'lodash/chunk';
import { selectBrushSelection } from '../../../core/selectors/graphMinimapBrush/graphMinimapBrushSelector';
import { selectGraphPeriodRange } from '../../../core/selectors/graphPeriod/graphPeriodSelector';

let graphDataRequestId = 0;
let graphDataRangeRequestId = 0;
let graphDataSources: CancelTokenSource[] = [];
let graphDataRangeSources: CancelTokenSource[] = [];

const cancelPendingRequests = (sources: CancelTokenSource[]): CancelTokenSource[] => {
    sources.forEach(source => source.cancel('Canceled stale graph request'));
    return [];
};

const normalizeTime = (value: string | Date): number => new Date(value).getTime();

const trimGraphPayloadToRange = (value: IHistogramData, from: string, to: string): IHistogramData => {
    const fromTime = normalizeTime(from);
    const toTime = normalizeTime(to);

    if (value.type === 'graph' && value.values) {
        return {
            ...value,
            values: value.values.filter(item => {
                const timestamp = normalizeTime(item.timestamp);
                return timestamp >= fromTime && timestamp <= toTime;
            }),
        };
    }

    if (value.states) {
        return {
            ...value,
            states: value.states.filter(item => {
                const startTime = item.startTime ? normalizeTime(item.startTime) : fromTime;
                const endTime = item.endTime ? normalizeTime(item.endTime) : toTime;

                return startTime <= toTime && endTime >= fromTime;
            }),
        };
    }

    return value;
};


/**
 * Graph data actions
 *
 * @type {Object}
 */
export const GraphDataAction = {

    /**
     * Show/hide state details popover
     *
     *
     * @return {Promise<Object>}
     * @param from
     * @param to
     */
    getHistogramData: (from: string, to: string): (dispatch: Dispatch<Record<string, unknown>, void, AnyAction>) => void => {

        const success = (graphDataMap: Map<number, IHistogramData>, showLoader: boolean) => {

            return {
                type: graphDataMapperConstants.GRAPH_DATA_MAPPER_SUCCESS,
                graphData: graphDataMap,
                showLoader: showLoader,
            };

        }, successRange = (graphDataRangeMap: Map<number, IHistogramData>) => {

            return {
                type: graphDataMapperConstants.GRAPH_DATA_RANGE_MAPPER_SUCCESS,
                graphDataRange: graphDataRangeMap,
            };

        }, failure = (errors: IErrors) => {

            return {
                type: graphDataMapperConstants.GRAPH_DATA_MAPPER_FAILURE,
            };
        }, service = new GraphAndStateDataService();

        return (dispatch: Dispatch<Record<string, unknown>, void, AnyAction>) => {

            const state = store.getState(),
                screenWidth = selectScreenWidth(state),
                graphDataMap = selectGraphDataAll(state),
                graphDataRangeMap = selectGraphDataRangeAll(state),
                brushSelection = selectBrushSelection(state),
                brushRange = selectGraphPeriodRange(state);

            const currentRequestId = ++graphDataRequestId;

            graphDataSources = cancelPendingRequests(graphDataSources);

            const [fromBS, toBS] = brushSelection;

            const { startDate, endDate } = brushRange;

            startDate.setSeconds(0);
            startDate.setMilliseconds(0);

            endDate.setSeconds(0);
            endDate.setMilliseconds(0);

            let allSensorInTree = chunk(Array.from(getAllSensor(state).values()), 20);

            if (allSensorInTree.length === 0) {

                allSensorInTree = [[]];
            }
            // dispatch(success(graphDataMap, true));
            allSensorInTree.map((item, index) =>{

                const sensorId = item.map(sensor=> sensor.id);
                const source = axios.CancelToken.source();

                graphDataSources.push(source);

                const argMain: IItemsOptions = {
                    from: from,
                    to: to,
                    numberOfDataPoints: screenWidth | 500,
                    sensors: sensorId,
                    units: [],
                };

                service.items(argMain, source.token)
                    .then(({ data }: AxiosResponse) => {
                        if (currentRequestId !== graphDataRequestId) {
                            return;
                        }

                        poolingGaps(data).then(poolingData => {
                            if (currentRequestId !== graphDataRequestId) {
                                return;
                            }

                            for (const value of poolingData) {
                                const trimmedValue = trimGraphPayloadToRange(value, from, to);
                                let showLoaderLogic = true;

                                if (trimmedValue.type === 'graph') {
                                    showLoaderLogic = trimmedValue.values.length > 0;
                                } else if (trimmedValue.states) {
                                    showLoaderLogic = trimmedValue.states.length > 0;
                                }

                                graphDataMap.delete(trimmedValue.id);
                                graphDataMap.set(trimmedValue.id, { ...trimmedValue, showLoader: showLoaderLogic });


                                if (fromBS.getTime() === startDate.getTime() && toBS.getTime() === endDate.getTime()) {
                                    
                                    graphDataRangeMap.delete(trimmedValue.id);
                                    graphDataRangeMap.set(trimmedValue.id, { ...trimmedValue, showLoader: showLoaderLogic });
                                }

                            }
                            if (fromBS.getTime() === startDate.getTime() && toBS.getTime() === endDate.getTime()) {

                                dispatch(successRange(graphDataRangeMap));
                            }
                            dispatch(success(graphDataMap, index === allSensorInTree.length));
                        });
                    })
                    .catch((error) => {
                        if (axios.isCancel(error) || currentRequestId !== graphDataRequestId) {
                            return;
                        }

                        dispatch(failure(service.errorHandler(error)));
                    });
            });
        };
    },

    /**
     * 
     * @param {string} from
     * @param {string} to
     * @return {(dispatch: ThunkDispatch<Record<string, unknown>, void, AnyAction>) => void}
     */
    getHistogramDataRange: (from: string, to: string): (dispatch: Dispatch<Record<string, unknown>, void, AnyAction>) => void  => {

        const successRange = (graphDataRangeMap: Map<number, IHistogramData>) => {

            return {
                type: graphDataMapperConstants.GRAPH_DATA_RANGE_MAPPER_SUCCESS,
                graphDataRange: graphDataRangeMap,
            };

        }, failure = (errors: IErrors) => {

            return {
                type: graphDataMapperConstants.GRAPH_DATA_MAPPER_FAILURE,
            };
        }, service = new GraphAndStateDataService();

        return (dispatch: Dispatch<Record<string, unknown>, void, AnyAction>) => {

            const state = store.getState(),
                screenWidth = selectScreenWidth(state),
                graphDataRangeMap = selectGraphDataRangeAll(state);

            const currentRequestId = ++graphDataRangeRequestId;

            graphDataRangeSources = cancelPendingRequests(graphDataRangeSources);

            let allSensorInTree = chunk(Array.from(getAllSensor(state).values()), 20);

            graphDataRangeMap.clear();

            if (allSensorInTree.length === 0) {

                allSensorInTree = [[]];
            }

            allSensorInTree.map(item =>{

                const sensorId = item.map(sensor=> sensor.id);
                const source = axios.CancelToken.source();

                graphDataRangeSources.push(source);

                const argMain: IItemsOptions = {
                    from: from,
                    to: to,
                    numberOfDataPoints: screenWidth | 500,
                    sensors: sensorId,
                    units: [],
                };

                service.items(argMain, source.token)
                    .then(({ data }: AxiosResponse) => {
                        if (currentRequestId !== graphDataRangeRequestId) {
                            return;
                        }

                        poolingGaps(data).then(poolingData => {
                            if (currentRequestId !== graphDataRangeRequestId) {
                                return;
                            }

                            for (const value of poolingData) {
                                const trimmedValue = trimGraphPayloadToRange(value, from, to);
                                // graphDataRangeMap.has(value.id) && graphDataRangeMap.delete(value.id);
                                let showLoaderLogic = true;

                                if (trimmedValue.type === 'graph') {

                                    showLoaderLogic = trimmedValue.values.length > 0;
                                } else if (trimmedValue.states) {

                                    showLoaderLogic = trimmedValue.states.length > 0;
                                }

                                graphDataRangeMap.delete(trimmedValue.id);
                                graphDataRangeMap.set(trimmedValue.id, { ...trimmedValue, showLoader: showLoaderLogic });
                                // graphDataRangeMap.set(value.id, value);

                            }

                            dispatch(successRange(graphDataRangeMap));
                        });
                    })
                    .catch((error) => {
                        if (axios.isCancel(error) || currentRequestId !== graphDataRangeRequestId) {
                            return;
                        }

                        dispatch(failure(service.errorHandler(error)));
                    });

            });
           
        };
    },
};
